// Diálogo modal simple (no ventanas flotantes). Esc lo cierra sin borrar nada; al cerrarse devuelve
// el foco a donde estaba. Mientras está abierto, los atajos de la pantalla de atrás no corren.
import { useEffect, useRef, type ReactNode } from 'react';

export function Modal({
  titulo,
  alCerrar,
  children,
  ancho = 'medio',
  pie,
  etiqueta,
  devolverFoco = true,
  ubicacion = 'arriba',
}: {
  titulo: string;
  alCerrar: () => void;
  children: ReactNode;
  ancho?: 'angosto' | 'medio' | 'ancho';
  pie?: ReactNode;
  etiqueta?: string;
  /** false: quien abre el diálogo decide adónde va el foco al cerrar (p. ej. el diálogo RTU). */
  devolverFoco?: boolean;
  /** «media»: más abajo que el resto (diálogos chicos de la grilla, como el RTU). */
  ubicacion?: 'arriba' | 'media';
}) {
  const caja = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previo = document.activeElement as HTMLElement | null;
    // Primero el elemento marcado (p. ej. «Confirmar»), si no el primer campo; nunca «Cerrar».
    const primero =
      caja.current?.querySelector<HTMLElement>('[data-autofoco]') ??
      caja.current?.querySelector<HTMLElement>(
        '.modal-cuerpo input, .modal-cuerpo select, .modal-cuerpo textarea',
      );
    // Sin campos (p. ej. la ayuda F1): el foco va al diálogo mismo, para que Esc lo cierre.
    (primero ?? caja.current)?.focus();
    return () => {
      if (devolverFoco) previo?.focus?.();
    };
  }, []);
  return (
    <div
      className={`modal-fondo ${ubicacion === 'media' ? 'modal-media' : ''}`}
      onMouseDown={(e) => e.target === e.currentTarget && alCerrar()}
    >
      <div
        ref={caja}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-label={etiqueta ?? titulo}
        className={`modal modal-${ancho}`}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            alCerrar();
          }
          // Ningún atajo de la pantalla de atrás mientras el diálogo está abierto.
          if (e.altKey || (e.key.startsWith('F') && e.key.length > 1)) e.stopPropagation();
        }}
      >
        <header className="modal-cabecera">
          <h2>{titulo}</h2>
          <button
            type="button"
            className="boton-icono"
            onClick={alCerrar}
            aria-label="Cerrar (Esc)"
          >
            ✕
          </button>
        </header>
        <div className="modal-cuerpo">{children}</div>
        {pie && <footer className="modal-pie">{pie}</footer>}
      </div>
    </div>
  );
}
